import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "logContainer",
    "logEntries",
    "loading",
    "noLogs",
    "error",
    "levelFilter",
    "timeRange",
    "searchInput",
    "tailButton",
    "tailLabel"
  ]

  static values = {
    instanceId: Number,
    fetchUrl: String
  }

  connect() {
    this.tailEnabled = false
    this.searchTimeout = null
    this.fetchLogs()
  }

  disconnect() {
    this.stopTailing()
  }

  async fetchLogs() {
    this.showLoading()

    try {
      const params = new URLSearchParams()

      if (this.hasLevelFilterTarget && this.levelFilterTarget.value) {
        params.append("level", this.levelFilterTarget.value)
      }

      if (this.hasTimeRangeTarget && this.timeRangeTarget.value) {
        params.append("timerange", this.timeRangeTarget.value)
      }

      if (this.hasSearchInputTarget && this.searchInputTarget.value) {
        params.append("search", this.searchInputTarget.value)
      }

      const url = `${this.fetchUrlValue}?${params.toString()}`
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      this.renderLogs(data.events)
    } catch (error) {
      console.error("Failed to fetch logs:", error)
      this.showError()
    }
  }

  renderLogs(events) {
    this.hideAllStates()

    if (!events || events.length === 0) {
      this.noLogsTarget.classList.remove("hidden")
      return
    }

    const html = events.map(event => this.renderLogEntry(event)).join("")
    this.logEntriesTarget.innerHTML = html
    this.logEntriesTarget.classList.remove("hidden")

    if (this.tailEnabled) {
      this.scrollToBottom()
    }
  }

  renderLogEntry(event) {
    const timestamp = new Date(event.timestamp).toLocaleTimeString()
    const levelClass = this.getLevelClass(event.message)
    const highlightedMessage = this.highlightSearch(event.message)

    return `
      <div class="log-entry flex space-x-2 hover:bg-gray-800/50 px-2 py-0.5 rounded">
        <span class="text-gray-500 flex-shrink-0">${timestamp}</span>
        <span class="${levelClass} flex-grow">${highlightedMessage}</span>
      </div>
    `
  }

  getLevelClass(message) {
    if (message.includes("[ERROR]") || message.includes("ERROR")) {
      return "text-red-400"
    } else if (message.includes("[WARN]") || message.includes("WARNING")) {
      return "text-yellow-400"
    } else if (message.includes("[DEBUG]")) {
      return "text-gray-500"
    }
    return "text-gray-300"
  }

  highlightSearch(message) {
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value : ""
    if (!searchTerm) return message

    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const regex = new RegExp(`(${escapedTerm})`, "gi")
    return message.replace(regex, '<mark class="bg-yellow-500/30 text-yellow-200">$1</mark>')
  }

  filterByLevel() {
    this.fetchLogs()
  }

  changeTimeRange() {
    this.fetchLogs()
  }

  debounceSearch() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      this.fetchLogs()
    }, 300)
  }

  refresh() {
    this.fetchLogs()
  }

  toggleTail() {
    this.tailEnabled = !this.tailEnabled

    if (this.tailEnabled) {
      this.tailLabelTarget.textContent = "Auto-scroll: On"
      this.tailButtonTarget.classList.add("text-indigo-400")
      this.startTailing()
    } else {
      this.tailLabelTarget.textContent = "Auto-scroll: Off"
      this.tailButtonTarget.classList.remove("text-indigo-400")
      this.stopTailing()
    }
  }

  startTailing() {
    this.tailInterval = setInterval(() => {
      this.fetchLogs()
    }, 5000)
  }

  stopTailing() {
    if (this.tailInterval) {
      clearInterval(this.tailInterval)
      this.tailInterval = null
    }
  }

  scrollToBottom() {
    this.logContainerTarget.scrollTop = this.logContainerTarget.scrollHeight
  }

  showLoading() {
    this.hideAllStates()
    this.loadingTarget.classList.remove("hidden")
  }

  showError() {
    this.hideAllStates()
    this.errorTarget.classList.remove("hidden")
  }

  hideAllStates() {
    this.loadingTarget.classList.add("hidden")
    this.logEntriesTarget.classList.add("hidden")
    this.noLogsTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
  }
}
